/**
 * ConnectedAccountsPage - Manage OAuth-connected email accounts
 *
 * Part of White-Glove Onboarding: allows users to connect their email
 * to enable automatic commission file extraction.
 */

import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  Mail,
  Plus,
  Trash2,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Clock,
  FileText,
  Loader2,
  ExternalLink,
  AtSign,
  Lock,
  ShieldAlert,
  Copy,
  Check,
} from 'lucide-react';
import { useEmailConnections, EmailConnection } from '../../hooks/useEmailConnections';
import { useCommissionSources } from '../../hooks/useCommissionSources';

/** Longest error string rendered inline on a connection card. */
const MAX_INLINE_ERROR_CHARS = 200;

const truncateError = (value?: string | null): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.length > MAX_INLINE_ERROR_CHARS
    ? `${trimmed.slice(0, MAX_INLINE_ERROR_CHARS)}...`
    : trimmed;
};

const providerLabel = (type: EmailConnection['type']): string =>
  type === 'outlook' ? 'Microsoft' : 'Gmail';

/**
 * Tenant admin-consent URL. Microsoft blocks user consent for an unverified
 * multi-tenant app requesting Mail.Read, so the organisation's Microsoft 365
 * administrator has to approve Valutten once for the whole directory.
 */
const buildAdminConsentUrl = (clientId: string, providerTenantId?: string): string =>
  `https://login.microsoftonline.com/${providerTenantId ?? 'common'}/adminconsent?client_id=${encodeURIComponent(clientId)}`;

// Status badge component
const StatusBadge: React.FC<{ status: EmailConnection['status'] }> = ({ status }) => {
  const statusConfig: Record<string, { color: string; label: string; icon: React.ReactNode }> = {
    active: {
      color: 'bg-green-500/20 text-green-400 border-green-500/30',
      label: 'Connected',
      icon: <CheckCircle size={14} />,
    },
    pending_auth: {
      color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      label: 'Pending',
      icon: <Clock size={14} />,
    },
    auth_exchanging: {
      color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      label: 'Connecting',
      icon: <Loader2 size={14} className="animate-spin" />,
    },
    paused: {
      color: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      label: 'Paused',
      icon: <Clock size={14} />,
    },
    error: {
      color: 'bg-red-500/20 text-red-400 border-red-500/30',
      label: 'Error',
      icon: <AlertCircle size={14} />,
    },
    reauth_required: {
      color: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      label: 'Reconnect required',
      icon: <AlertCircle size={14} />,
    },
    admin_consent_required: {
      color: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      label: 'Admin approval needed',
      icon: <ShieldAlert size={14} />,
    },
    auth_failed: {
      color: 'bg-red-500/20 text-red-400 border-red-500/30',
      label: 'Connection failed',
      icon: <AlertCircle size={14} />,
    },
    revoked: {
      color: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      label: 'Disconnected',
      icon: <AlertCircle size={14} />,
    },
    pending_invite: {
      color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      label: 'Invite Sent',
      icon: <Mail size={14} />,
    },
  };

  const config = statusConfig[status] || statusConfig.error;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.color}`}>
      {config.icon}
      {config.label}
    </span>
  );
};

/** Admin-consent instructions plus the copyable consent URL. */
const AdminConsentPanel: React.FC<{ consentUrl: string | null }> = ({ consentUrl }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!consentUrl) return;
    try {
      await navigator.clipboard?.writeText(consentUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy admin consent link:', err);
    }
  };

  return (
    <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
      <div className="flex items-start gap-2">
        <ShieldAlert className="text-amber-400 mt-0.5 flex-shrink-0" size={16} />
        <div className="min-w-0">
          <h4 className="text-sm font-medium text-amber-300">Microsoft 365 administrator approval needed</h4>
          <p className="text-xs text-amber-200/70 mt-1">
            Your organisation's Microsoft 365 administrator must approve Valutten for your directory
            before this mailbox can be connected. Send them the link below, or ask them to open it
            while signed in as an administrator.
          </p>
          {consentUrl ? (
            <div className="mt-2 flex items-center gap-2">
              <code
                data-testid="admin-consent-url"
                className="flex-1 min-w-0 truncate text-xs text-amber-200 bg-gray-900/50 border border-amber-500/20 rounded px-2 py-1"
              >
                {consentUrl}
              </code>
              <button
                type="button"
                onClick={handleCopy}
                className="px-2 py-1 text-xs text-amber-300 hover:text-amber-200 hover:bg-amber-500/10 rounded transition-colors flex items-center gap-1 flex-shrink-0"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? 'Copied' : 'Copy link'}
              </button>
            </div>
          ) : (
            <p className="mt-2 text-xs text-amber-200/70">
              The approval link is unavailable right now. Please contact Valutten support.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Connection card component
const ConnectionCard: React.FC<{
  connection: EmailConnection;
  onRevoke: (id: string) => void;
  onReconnect: (connection: EmailConnection) => void;
  isRevoking: boolean;
  isConnecting: boolean;
  microsoftClientId: string | null;
}> = ({ connection, onRevoke, onReconnect, isRevoking, isConnecting, microsoftClientId }) => {
  const [showConfirm, setShowConfirm] = useState(false);

  const handleRevoke = () => {
    if (showConfirm) {
      onRevoke(connection.connectionId);
      setShowConfirm(false);
    } else {
      setShowConfirm(true);
    }
  };

  const getProviderIcon = () => {
    if (connection.type === 'gmail') {
      return (
        <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
          <Mail className="text-red-400" size={20} />
        </div>
      );
    }
    return (
      <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
        <Mail className="text-blue-400" size={20} />
      </div>
    );
  };

  const isOutlook = connection.type === 'outlook';
  const canReconnect = isOutlook
    && (connection.status === 'reauth_required' || connection.status === 'error' || connection.status === 'auth_failed');
  const syncError = truncateError(connection.lastSyncError);
  const authError = truncateError(connection.lastAuthError);

  return (
    <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5 hover:border-gray-600 transition-all">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          {getProviderIcon()}
          <div>
            <h3 className="font-semibold text-white">{connection.userEmail}</h3>
            <p className="text-sm text-gray-400">{providerLabel(connection.type)}</p>
          </div>
        </div>
        <StatusBadge status={connection.status} />
      </div>

      {/* Stats */}
      <div className="mt-4 grid grid-cols-3 gap-4">
        <div className="text-center p-3 bg-gray-900/50 rounded-lg">
          <div className="text-xl font-bold text-white">{connection.filesExtracted}</div>
          <div className="text-xs text-gray-400">Files Extracted</div>
        </div>
        <div className="text-center p-3 bg-gray-900/50 rounded-lg">
          <div className="text-xl font-bold text-white">{connection.messagesProcessed}</div>
          <div className="text-xs text-gray-400">Emails Scanned</div>
        </div>
        <div className="text-center p-3 bg-gray-900/50 rounded-lg">
          <div className="text-xs font-medium text-white">
            {connection.lastSyncAt
              ? new Date(connection.lastSyncAt).toLocaleDateString()
              : 'Never'}
          </div>
          <div className="text-xs text-gray-400">Last Sync</div>
        </div>
      </div>

      {/* Last sync result */}
      {connection.lastSyncResult && connection.lastSyncResult !== 'success' && (
        <div className="mt-3 p-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
          <p className="text-xs text-yellow-400">
            Last sync: {connection.lastSyncResult === 'partial' ? 'Completed with some errors' : 'Failed'}
          </p>
          {syncError && (
            <p data-testid="last-sync-error" className="text-xs text-yellow-200/70 mt-1 break-words">
              {syncError}
            </p>
          )}
        </div>
      )}

      {/* Authentication failure detail */}
      {connection.status === 'auth_failed' && authError && (
        <div className="mt-3 p-2 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p data-testid="last-auth-error" className="text-xs text-red-300 break-words">
            {authError}
          </p>
        </div>
      )}

      {/* Admin consent */}
      {connection.status === 'admin_consent_required' && (
        <AdminConsentPanel
          consentUrl={
            microsoftClientId
              ? buildAdminConsentUrl(microsoftClientId, connection.providerTenantId)
              : null
          }
        />
      )}

      {/* Actions */}
      <div className="mt-4 flex justify-end items-center gap-2">
        {canReconnect && !showConfirm && (
          <button
            onClick={() => onReconnect(connection)}
            disabled={isConnecting}
            className="px-3 py-1.5 text-sm text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50"
          >
            {isConnecting ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Reconnect
          </button>
        )}
        {showConfirm ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-400">Disconnect this account?</span>
            <button
              onClick={() => setShowConfirm(false)}
              className="px-3 py-1.5 text-sm text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleRevoke}
              disabled={isRevoking}
              className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center gap-1"
            >
              {isRevoking ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Confirm
            </button>
          </div>
        ) : (
          <button
            onClick={handleRevoke}
            className="px-3 py-1.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors flex items-center gap-1"
          >
            <Trash2 size={14} />
            Disconnect
          </button>
        )}
      </div>
    </div>
  );
};

export const ConnectedAccountsContent: React.FC<{ companyData: any; userData: any }> = ({ companyData, userData }) => {
  const companyId = companyData?.id;

  const {
    connections,
    availableProviders,
    microsoftClientId,
    loading,
    error,
    refetch,
    initiateOAuth,
    revokeConnection,
    isConnecting,
    isRevoking,
  } = useEmailConnections(companyId);

  const {
    builtInSenders,
    companySources,
    loading: sourcesLoading,
    addSource,
    removeSource,
    isSubmitting,
  } = useCommissionSources(companyId);

  const [connectError, setConnectError] = useState<string | null>(null);
  const [newSenderEmail, setNewSenderEmail] = useState('');
  const [newSenderName, setNewSenderName] = useState('');
  const [showMicrosoftRevokeNote, setShowMicrosoftRevokeNote] = useState(false);

  const showCommissionSenders = userData?.role === 'super_admin' || userData?.role === 'administrator';
  const outlookAvailable = availableProviders.includes('outlook');

  const handleAddSource = async () => {
    if (!newSenderEmail) return;
    try {
      await addSource(newSenderEmail.trim(), newSenderName.trim() || undefined);
      setNewSenderEmail('');
      setNewSenderName('');
    } catch (err: any) {
      setConnectError(err.message || 'Failed to add sender');
    }
  };

  const handleRemoveSource = async (sourceId: string) => {
    try {
      await removeSource(sourceId);
    } catch (err: any) {
      setConnectError(err.message || 'Failed to remove sender');
    }
  };

  const handleConnectGmail = async () => {
    try {
      setConnectError(null);
      const authUrl = await initiateOAuth('gmail');
      // Redirect to Google OAuth
      window.location.href = authUrl;
    } catch (err: any) {
      setConnectError(err.message || 'Failed to initiate connection');
    }
  };

  const handleConnectMicrosoft = async () => {
    // Guard: while the rollout gate withholds Outlook the card is inert, so the
    // backend is never called from a tenant that cannot use it.
    if (!outlookAvailable) return;
    try {
      setConnectError(null);
      const authUrl = await initiateOAuth('outlook');
      window.location.href = authUrl;
    } catch (err: any) {
      setConnectError(err.message || 'Failed to initiate connection');
    }
  };

  const handleReconnect = async (connection: EmailConnection) => {
    try {
      setConnectError(null);
      const authUrl = await initiateOAuth(connection.type, { reconnectOf: connection.connectionId });
      window.location.href = authUrl;
    } catch (err: any) {
      setConnectError(err.message || 'Failed to start reconnection');
    }
  };

  const handleRevokeConnection = async (connectionId: string) => {
    const wasOutlook = connections.some(
      c => c.connectionId === connectionId && c.type === 'outlook'
    );
    try {
      await revokeConnection(connectionId);
      if (wasOutlook) {
        setShowMicrosoftRevokeNote(true);
      }
    } catch (err: any) {
      setConnectError(err.message || 'Failed to disconnect account');
    }
  };

  // Check for success/error from OAuth callback
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const email = params.get('email');
    const errorParam = params.get('error');

    if (success === 'true' && email) {
      // Clear URL params and refetch
      window.history.replaceState({}, '', window.location.pathname);
      refetch();
    } else if (errorParam) {
      setConnectError(`Connection failed: ${errorParam}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [refetch]);

  const activeConnections = connections.filter(c => c.status === 'active');

  return (
    <div className="space-y-6">
        {/* Error Banner */}
        {(error || connectError) && (
          <div className="mb-6 bg-red-900/50 border border-red-500 text-red-200 p-4 rounded-lg flex items-center gap-2">
            <AlertCircle size={20} />
            {error || connectError}
          </div>
        )}

        {/* Microsoft revoke follow-up */}
        {showMicrosoftRevokeNote && (
          <div
            data-testid="microsoft-revoke-note"
            className="mb-6 bg-blue-900/30 border border-blue-500/30 p-4 rounded-lg"
          >
            <div className="flex items-start gap-3">
              <ShieldAlert className="text-blue-400 mt-0.5" size={20} />
              <div>
                <h3 className="font-medium text-blue-300">Microsoft account disconnected</h3>
                <p className="text-sm text-blue-200/70 mt-1">
                  Valutten has deleted its stored access for this mailbox. Microsoft does not offer a
                  remote revoke, so you may also want to remove Valutten from your Microsoft account
                  under "Apps and services" (or "My Apps" in Microsoft Entra).
                </p>
                <div className="mt-2 flex flex-wrap gap-3">
                  <a
                    href="https://myapps.microsoft.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-300 hover:text-blue-200 underline flex items-center gap-1"
                  >
                    My Apps (work or school)
                    <ExternalLink size={12} />
                  </a>
                  <a
                    href="https://account.microsoft.com/privacy/app-access"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-300 hover:text-blue-200 underline flex items-center gap-1"
                  >
                    App access (personal account)
                    <ExternalLink size={12} />
                  </a>
                </div>
                <button
                  onClick={() => setShowMicrosoftRevokeNote(false)}
                  className="mt-2 text-xs text-blue-300/70 hover:text-blue-200"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Info Banner */}
        <div className="mb-6 bg-blue-900/30 border border-blue-500/30 p-4 rounded-lg">
          <div className="flex items-start gap-3">
            <FileText className="text-blue-400 mt-0.5" size={20} />
            <div>
              <h3 className="font-medium text-blue-300">How it works</h3>
              <p className="text-sm text-blue-200/70 mt-1">
                We scan your email for commission statements from known aggregators (Loan Market, AFG, WLTH, Orde, etc.)
                and automatically extract the data. Your emails are never stored - only commission file attachments are processed.
              </p>
            </div>
          </div>
        </div>

        {/* Connect New Account */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Plus size={20} />
            Connect New Account
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Gmail */}
            <button
              onClick={handleConnectGmail}
              disabled={isConnecting}
              className="flex items-center gap-4 p-5 bg-gray-800/50 border border-gray-700 rounded-xl hover:border-red-500/50 hover:bg-gray-800 transition-all disabled:opacity-50"
            >
              <div className="w-12 h-12 rounded-lg bg-red-500/20 flex items-center justify-center">
                {isConnecting ? (
                  <Loader2 className="text-red-400 animate-spin" size={24} />
                ) : (
                  <Mail className="text-red-400" size={24} />
                )}
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-white">Connect Gmail</h3>
                <p className="text-sm text-gray-400">Google Workspace or personal Gmail</p>
              </div>
              <ExternalLink className="ml-auto text-gray-500" size={16} />
            </button>

            {/* Microsoft */}
            <button
              onClick={outlookAvailable ? handleConnectMicrosoft : undefined}
              disabled={!outlookAvailable || isConnecting}
              className="flex items-center gap-4 p-5 bg-gray-800/50 border border-gray-700 rounded-xl hover:border-blue-500/50 hover:bg-gray-800 transition-all disabled:opacity-50"
            >
              <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
                {outlookAvailable && isConnecting ? (
                  <Loader2 className="text-blue-400 animate-spin" size={24} />
                ) : (
                  <Mail className="text-blue-400" size={24} />
                )}
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-white">
                  {outlookAvailable ? 'Connect Microsoft' : 'Connect Outlook'}
                </h3>
                <p className="text-sm text-gray-400">Microsoft 365 or Outlook.com</p>
              </div>
              {outlookAvailable ? (
                <ExternalLink className="ml-auto text-gray-500" size={16} />
              ) : (
                <span className="ml-auto text-xs text-gray-500 bg-gray-700 px-2 py-1 rounded">Coming Soon</span>
              )}
            </button>
          </div>
        </div>

        {/* Connected Accounts */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <CheckCircle size={20} />
              Connected Accounts ({activeConnections.length})
            </h2>
            <button
              onClick={() => refetch()}
              disabled={loading}
              className="px-3 py-1.5 text-sm text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-1"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="animate-spin text-gray-400" size={32} />
            </div>
          ) : connections.length === 0 ? (
            <div className="text-center p-12 bg-gray-800/30 border border-gray-700 rounded-xl">
              <Mail className="mx-auto text-gray-500 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-300 mb-2">No accounts connected</h3>
              <p className="text-gray-500">
                Connect your email to start importing commission data automatically.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {connections.map((connection) => (
                <ConnectionCard
                  key={connection.connectionId}
                  connection={connection}
                  onRevoke={handleRevokeConnection}
                  onReconnect={handleReconnect}
                  isRevoking={isRevoking}
                  isConnecting={isConnecting}
                  microsoftClientId={microsoftClientId}
                />
              ))}
            </div>
          )}
        </div>

        {/* Commission Senders */}
        {showCommissionSenders && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <AtSign size={20} />
              Commission Senders
            </h2>

            <p className="text-sm text-gray-400 mb-4">
              Built-in senders are always scanned. Custom senders extend this list for your company.
            </p>

            {sourcesLoading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="animate-spin text-gray-400" size={24} />
              </div>
            ) : (
              <>
                {/* Built-in senders (read-only) */}
                <div className="space-y-2 mb-6">
                  <h3 className="text-sm font-medium text-gray-300">Built-in Senders</h3>
                  {builtInSenders.map(email => (
                    <div key={email} className="flex items-center gap-2 px-3 py-2 bg-gray-800/30 border border-gray-700 rounded-lg">
                      <Lock size={14} className="text-gray-500" />
                      <span className="text-sm text-gray-300">{email}</span>
                      <span className="ml-auto text-xs text-gray-500 bg-gray-700 px-2 py-0.5 rounded">Built-in</span>
                    </div>
                  ))}
                </div>

                {/* Custom senders (editable) */}
                <div className="space-y-2 mb-6">
                  <h3 className="text-sm font-medium text-gray-300">Custom Senders</h3>
                  {companySources.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No custom senders configured.</p>
                  ) : (
                    companySources.map(source => (
                      <div key={source.sourceId} className="flex items-center gap-2 px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg">
                        <Mail size={14} className="text-blue-400" />
                        <span className="text-sm text-white">{source.senderEmail}</span>
                        {source.displayName && <span className="text-xs text-gray-400">({source.displayName})</span>}
                        <button onClick={() => handleRemoveSource(source.sourceId)} className="ml-auto text-red-400 hover:text-red-300">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))
                  )}
                </div>

                {/* Add new sender form */}
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={newSenderEmail}
                    onChange={e => setNewSenderEmail(e.target.value)}
                    placeholder="commissions@newlender.com.au"
                    className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                  />
                  <input
                    type="text"
                    value={newSenderName}
                    onChange={e => setNewSenderName(e.target.value)}
                    placeholder="Display name (optional)"
                    className="w-48 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                  />
                  <button
                    onClick={handleAddSource}
                    disabled={!newSenderEmail || isSubmitting}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                  >
                    {isSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                    Add
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Privacy Note */}
        <div className="mt-8 p-4 bg-gray-800/30 border border-gray-700 rounded-lg">
          <p className="text-xs text-gray-500">
            <strong className="text-gray-400">Privacy:</strong> We only access emails from known commission senders.
            Your email content is never stored. Only commission file attachments (CSV, XLSX, PDF) are processed.
            You can disconnect your account at any time.
          </p>
        </div>
    </div>
  );
};

/**
 * Route component — redirects /settings/connected-accounts to /settings?tab=connected-accounts
 * preserving any OAuth callback params (success, email, error).
 */
const ConnectedAccountsRedirect: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('tab', 'connected-accounts');
  return <Navigate to={`/settings?${params.toString()}`} replace />;
};

export default ConnectedAccountsRedirect;
